import {getAddToCartForm, isDisplayed} from 'utilities/product';
import {
  trackDeliveryPromiseRenderEvent,
  trackUnableToMount,
} from 'utilities/tracking';
import {ShopPromisePerformance} from 'utilities/telemetry/metrics';

import {validateSlot} from './helpers';

export const DYNAMIC_CHECKOUT_BUTTON_TIMEOUT_IN_MS = 2000;

type MountingPoint =
  | {
      container: HTMLElement;
      strategy:
        | 'delivery-promise-container'
        | 'dynamic-checkout-button-slot'
        | 'add-to-cart-form';
    }
  | {
      container?: undefined;
      strategy: 'none';
    };

export function getInitialMountingPoint(): MountingPoint {
  let container = document.querySelector<HTMLElement>(
    '.delivery-promise__promise-container',
  );

  if (container) {
    return {
      strategy: 'delivery-promise-container',
      container,
    };
  }

  container = getAddToCartForm();
  return container
    ? {strategy: 'add-to-cart-form', container}
    : {strategy: 'none'};
}

export function mountWebComponent() {
  const webComponentElement = createWebComponentElement();
  const {strategy, container} = getInitialMountingPoint();
  if (strategy === 'none') {
    const themeName =
      window.BOOMR?.themeName || window.Shopify.theme?.name || null;
    trackUnableToMount(themeName);
    trackDeliveryPromiseRenderEvent('', 'theme_incompatibility', 'hidden');
    return;
  }

  container.appendChild(webComponentElement);
  webComponentElement.setAttribute('data-strategy', strategy);
  const themeOverride = container?.dataset?.theme ?? null;

  if (themeOverride) {
    webComponentElement.setAttribute('data-theme', themeOverride);
  }

  if (strategy === 'add-to-cart-form') {
    const dynamicCheckoutButton = getDynamicCheckoutButton();
    if (
      dynamicCheckoutButton &&
      isDisplayed(dynamicCheckoutButton, false) &&
      validateSlot(dynamicCheckoutButton)
    ) {
      moveModuleToShopPromiseDynamicCheckout(
        webComponentElement,
        dynamicCheckoutButton,
      );
      return;
    }
  }
  ShopPromisePerformance.measure('mountingPoint', {
    metadata: {reason: 'none'},
  });
}

export function getDynamicCheckoutButton() {
  return document.querySelector<HTMLDivElement>(
    '[data-shopify=payment-button]',
  );
}

export function moveModuleToShopPromiseDynamicCheckout(
  webComponentElement: HTMLElement,
  dynamicCheckoutButton: HTMLElement,
) {
  const moveModule = (container: HTMLElement) => {
    webComponentElement.setAttribute(
      'data-strategy',
      'dynamic-checkout-button-slot',
    );
    container.appendChild(webComponentElement);
    webComponentElement.style.display = 'block';
    ShopPromisePerformance.measure('mountingPoint', {
      metadata: {reason: 'none'},
    });
  };

  webComponentElement.style.display = 'none';
  let dynamicCheckoutButtonSlot = getDynamicCheckoutButtonSlot(
    dynamicCheckoutButton,
  );

  if (dynamicCheckoutButtonSlot) {
    moveModule(dynamicCheckoutButtonSlot);
    return;
  }

  // eslint-disable-next-line prefer-const
  let timeoutId: NodeJS.Timeout;

  const observer = new MutationObserver((mutations) => {
    if (mutations.length > 0) {
      dynamicCheckoutButtonSlot = getDynamicCheckoutButtonSlot(
        dynamicCheckoutButton,
      );

      if (dynamicCheckoutButtonSlot) {
        clearTimeout(timeoutId);
        observer.disconnect();
        moveModule(dynamicCheckoutButtonSlot);
      }
    }
  });

  observer.observe(dynamicCheckoutButton, {
    childList: true,
    subtree: true,
  });

  timeoutId = setTimeout(() => {
    webComponentElement.style.display = 'block';
    ShopPromisePerformance.measure('mountingPoint', {
      metadata: {reason: 'dynamic-checkout-timeout'},
    });
    observer.disconnect();
  }, DYNAMIC_CHECKOUT_BUTTON_TIMEOUT_IN_MS);
}

function getDynamicCheckoutButtonSlot(dynamicCheckoutButton: Element) {
  return dynamicCheckoutButton.querySelector<HTMLDivElement>(
    '[data-shopify=shop-promise-payment-button-slot]',
  );
}

export function createWebComponentElement() {
  const webComponent = document.createElement('delivery-promise-wc');
  webComponent.setAttribute('slot', 'promise');
  const genericPromiseSlot = document.createElement('div');
  genericPromiseSlot.setAttribute('slot', 'generic-promise');
  genericPromiseSlot.id = 'generic-promise-wc';
  webComponent.appendChild(genericPromiseSlot);

  return webComponent;
}
